import React, { useSyncExternalStore } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
    CheckCircleOutline,
    Close,
    ErrorOutline,
    InfoOutlined,
    WarningAmberOutlined,
} from "@mui/icons-material";
import { AnimatePresence, motion } from "framer-motion";
import { colors } from "../../theme/colors";
import {
    dismissToast,
    getToastSnapshot,
    subscribeToasts,
} from "../../stores/toastStore";

const MotionBox = motion.create ? motion.create(Box) : motion(Box);
const COUNTDOWN_HEIGHT = "2px";
/** RankCard-style slant — applied via skew so the draining edge stays angled. */
const COUNTDOWN_SKEW = "skewX(-28deg)";
const TYPE_META = {
    success: {
        accent: colors.state.success.main,
        Icon: CheckCircleOutline,
    },
    error: {
        accent: colors.state.danger.main,
        Icon: ErrorOutline,
    },
    warning: {
        accent: colors.state.warning.main,
        Icon: WarningAmberOutlined,
    },
    info: {
        accent: colors.primary.main,
        Icon: InfoOutlined,
    },
};

/** Single parallelogram countdown line, flush full-width on the toast bottom edge. */
function ToastCountdownLine({ accent, durationMs }) {
    const safeDuration = Math.max(1, Number(durationMs) || 3000);

    return (
        <Box
            aria-hidden
            className="toast-countdown"
            sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: COUNTDOWN_HEIGHT,
                zIndex: 5,
                pointerEvents: "none",
                overflow: "hidden",
                "@keyframes ouroToastCountdownDrain": {
                    "0%": { transform: `${COUNTDOWN_SKEW} scaleX(1)` },
                    "100%": { transform: `${COUNTDOWN_SKEW} scaleX(0)` },
                },
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    left: "-6px",
                    right: "-6px",
                    top: 0,
                    bottom: 0,
                    transform: COUNTDOWN_SKEW,
                    transformOrigin: "left center",
                    backgroundColor: alpha(accent, 0.18),
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    left: "-6px",
                    right: "-6px",
                    top: 0,
                    bottom: 0,
                    transformOrigin: "left center",
                    background: `linear-gradient(90deg, ${alpha(accent, 0.75)}, ${accent})`,
                    animation: `ouroToastCountdownDrain ${safeDuration}ms linear forwards`,
                    willChange: "transform",
                }}
            />
        </Box>
    );
}

ToastCountdownLine.propTypes = {
    accent: PropTypes.string.isRequired,
    durationMs: PropTypes.number.isRequired,
};

function ToastItem({ toast }) {
    const meta = TYPE_META[toast.type] || TYPE_META.info;
    const { Icon, accent } = meta;

    return (
        <MotionBox
            initial={{ opacity: 0, y: -14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            role="status"
            aria-live="polite"
            sx={{
                position: "relative",
                width: "min(26rem, calc(100vw - 1.5rem))",
                borderRadius: "none",
                overflow: "hidden",
                pointerEvents: "auto",
                borderBottomLeftRadius: `10px`,
                borderBottomRightRadius: `10px`,
                background: `
                    linear-gradient(
                        165deg,
                        ${alpha(colors.background.card, 0.98)} 0%,
                        ${alpha(colors.background.surface, 0.96)} 50%,
                        ${alpha(colors.background.dark, 0.98)} 100%
                    )
                `,
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                boxShadow: `
                    0 14px 36px rgba(0, 0, 0, 0.48),
                    0 1px 0 ${alpha("#fff", 0.04)} inset
                `,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    pl: "0.85rem",
                    pr: "0.45rem",
                    pt: "0.6rem",
                    pb: "0.65rem",
                    minHeight: "2.75rem",
                    boxSizing: "border-box",
                    position: "relative",
                    zIndex: 1,
                }}
            >
                <Icon
                    aria-hidden
                    sx={{
                        fontSize: "1.05rem",
                        flexShrink: 0,
                        color: accent,
                    }}
                />

                <Typography
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        color: alpha(colors.text.primary, 0.94),
                        fontSize: "0.82rem",
                        fontWeight: 600,
                        letterSpacing: "0.02em",
                        lineHeight: 1.35,
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                    }}
                >
                    {toast.message}
                </Typography>

                <IconButton
                    size="small"
                    aria-label="Dismiss notification"
                    onClick={() => dismissToast(toast.id)}
                    sx={{
                        width: "1.4rem",
                        height: "1.4rem",
                        flexShrink: 0,
                        borderRadius: "6px",
                        color: alpha(colors.text.secondary, 0.85),
                        backgroundColor: "rgba(255,255,255,0.03)",
                        "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.07)",
                            color: colors.text.primary,
                        },
                    }}
                >
                    <Close sx={{ fontSize: "0.85rem" }} />
                </IconButton>
            </Box>

            <ToastCountdownLine
                accent={accent}
                durationMs={toast.durationMs}
            />
        </MotionBox>
    );
}

ToastItem.propTypes = {
    toast: PropTypes.shape({
        id: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        message: PropTypes.string.isRequired,
        durationMs: PropTypes.number.isRequired,
        createdAt: PropTypes.number.isRequired,
    }).isRequired,
};

export default function Toast() {
    const toasts = useSyncExternalStore(
        subscribeToasts,
        getToastSnapshot,
        getToastSnapshot,
    );

    return (
        <Box
            aria-label="Notifications"
            sx={{
                position: "fixed",
                top: { xs: "0.75rem", xl: "1rem" },
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 14000,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "0.45rem",
                width: "max-content",
                maxWidth: "calc(100vw - 1rem)",
                pointerEvents: "none",
            }}
        >
            <AnimatePresence initial={false}>
                {toasts.map((item) => (
                    <ToastItem key={item.id} toast={item} />
                ))}
            </AnimatePresence>
        </Box>
    );
}
